import { omitBy, uniqBy } from 'lodash-es';
import type { Types } from '../../types';
import type { ConnectedTypes } from '../../make-connected';
import { getVertexId } from '../../../core/util';
import { idOf } from './util/util';
import { makeChainable } from '../../../common/make-chainable';
import type { GroupWithExclude } from '../../../core/interfaces/vertices/util/edges';


function fromPersonsToIndividuals (
  persons:  ConnectedTypes.person[],
  options:  Types.parsedToOptions
): Types.Entities.Individual[] {
  return persons.map(person => {
    const doc: Types.Entities.Individual = {
      id:    idOf.person(person, options),
      group: person.group ? `groups.${ getVertexId(person.group, options) }` : undefined,
    };

    if (options.includeEntityMeta) {
      doc.name = person.displayName;
    }

    return omitBy(doc, x => x == null) as Types.Entities.Individual;
  });
}

function fromGroupToIndividualsSet (
  id:        string,
  reference: GroupWithExclude.Util.Value<ConnectedTypes.group, ConnectedTypes.person>,
  options:   Types.parsedToOptions
): Types.Entities.IndividualsSet {
  const members   = new Set(reference.to.members?.map(x => getVertexId(x, options)));
  const excluded  = new Set(reference.exclude?.map(x => getVertexId(x, options)));
  const remaining = members.difference(excluded);

  const doc: Types.Entities.IndividualsSet = {
    id:          id,
    individuals: [...remaining].map(x => idOf.person(x, options)),
  };

  if (options.includeEntityMeta) {
    doc.name = reference.to.displayName + (reference.exclude?.length ? ` (excl. ${ reference.exclude.map(x => x.displayName).join(', ') })` : '');
  }

  return doc;
}

export function extractUniqueIndividuals (
  persons:     ConnectedTypes.person[],
  courses:     ConnectedTypes.course[],
  events:      ConnectedTypes.event[],
  lockedTimes: ConnectedTypes.lockedTime[],
  options:     Types.parsedToOptions
): (Types.Entities.Individual | Types.Entities.IndividualsSet)[] {
  const individuals = fromPersonsToIndividuals(persons, options);

  const uniqueIndividualsSetsFromGroupReferences = makeChainable([...courses, ...events, ...lockedTimes])
    .chain(
      x => x
        .map((item): GroupWithExclude.Util.Value<ConnectedTypes.group, ConnectedTypes.person>[] | undefined | null => {
          if ('groups'    in item) return item.groups;   // we may ignore the parent groups as they are all plain group references
          if ('coalesced' in item) return item.coalesced
            ?.filter(x => x.toModel == 'groups');
          return;
        })
        .flatMap(references => (references ?? [])
          .filter(x => x.to.species != 'class' || x.exclude?.length)   // if plain group reference we will use the group directly
          .map(x => ({ id: idOf.groupReference(x, options), value: x }))
        ),
      x => uniqBy(x, x => x.id)
        .map(x => fromGroupToIndividualsSet(x.id, x.value, options))
    )
    .value;

  return individuals.concat(uniqueIndividualsSetsFromGroupReferences);
};
